import { DuplicateQuotationIcon } from "components/ui/icons/quotations/DucplicateQuotationIcon"
import { PencilIcon } from "components/ui/icons/objectsTable/dropdown/PencilIcon"
import { DownloadIcon } from "components/ui/icons/download/DownloadIcon"
import { RESULT_CSV_MESSAGES } from "helpers/resultCsv.constants"
import { RESULT_PDF_MESSAGES } from "helpers/resultPdf.constants"
import { DETAILS_WORD_MESSAGES } from "helpers/detailsWord.constants"
import type { MenuProps } from "antd"
import style from "./TableQuotations.module.scss"

const { dropdownIcon, dropdownIconDownload } = style

type Props = {
	renameQuotation: (quotationId: number) => void
	duplicateQuotation: (quotationId: number) => void
	downloadResultCsv: (args: { quotationId: number; quotationName: string; drawingName?: string | null }) => void
	// On-demand annotated result PDF (#1401) — independent action from the CSV above, own busy flag.
	downloadResultPdf: (args: { quotationId: number; quotationName: string; drawingName?: string | null }) => void
	// On-demand Quotation Details Word file (#1448) — likewise independent, with its own busy flag.
	downloadDetailsWord: (args: { quotationId: number; quotationName: string; drawingName?: string | null }) => void
	// Name of the original PDF, one half of every download's name.
	drawingName?: string | null
	// Which quotation's CSV is generating, if any — shared across every surface offering the action.
	csvBusyQuotationId: number | null
	// Same, for the PDF — a SEPARATE flag: the two builds are independent backend jobs.
	pdfBusyQuotationId: number | null
	// Same, for the Word file — its own flag too.
	wordBusyQuotationId: number | null
}

type TDropdownItemsArgs = {
	quotationId: number
	quotationName: string
	isSubmitted: boolean
}

// Actions are owned by the parent (Quotations) so both creation paths share one project-number
// gate (REQ-377); this builder only assembles the menu.
export const TableQuotationsDropdownItems = ({
	renameQuotation,
	duplicateQuotation,
	downloadResultCsv,
	downloadResultPdf,
	downloadDetailsWord,
	drawingName,
	csvBusyQuotationId,
	pdfBusyQuotationId,
	wordBusyQuotationId,
}: Props) => {
	const getDropdownItems = ({ quotationId, quotationName, isSubmitted }: TDropdownItemsArgs): MenuProps["items"] => {
		const ownerItems: MenuProps["items"] = [
			{
				key: "1",
				label: "Duplicate and start quotation",
				onClick: () => duplicateQuotation(quotationId),
				icon: <DuplicateQuotationIcon className={dropdownIcon} />,
			},
			{
				key: "2",
				label: "Rename",
				icon: <PencilIcon className={dropdownIcon} />,
				onClick: () => renameQuotation(quotationId),
			},
		]

		// All three endpoints reject a submitted quotation with 409 — its result files are in the
		// expanded row instead, so none of these items is offered there.
		const isCsvPendingForThisRow = csvBusyQuotationId === quotationId
		const isPdfPendingForThisRow = pdfBusyQuotationId === quotationId
		const isWordPendingForThisRow = wordBusyQuotationId === quotationId
		const downloadItems: MenuProps["items"] = isSubmitted
			? []
			: [
					{
						key: "downloadResultCsv",
						// The toast carries progress once the menu closes; this is what a user who reopens the
						// menu mid-generation sees. Only the generating row says "Preparing" — the others are
						// disabled but keep their normal label.
						label: isCsvPendingForThisRow ? RESULT_CSV_MESSAGES.pending : RESULT_CSV_MESSAGES.menuItem,
						icon: <DownloadIcon className={`${dropdownIcon} ${dropdownIconDownload}`} strokeColor="black" />,
						// One CSV generation at a time — including from another row's menu, or the editor.
						disabled: csvBusyQuotationId !== null,
						onClick: () => downloadResultCsv({ quotationId, quotationName, drawingName }),
					},
					{
						key: "downloadResultPdf",
						label: isPdfPendingForThisRow ? RESULT_PDF_MESSAGES.pending : RESULT_PDF_MESSAGES.menuItem,
						icon: <DownloadIcon className={`${dropdownIcon} ${dropdownIconDownload}`} strokeColor="black" />,
						// One PDF generation at a time — independent of the CSV's own flag above.
						disabled: pdfBusyQuotationId !== null,
						onClick: () => downloadResultPdf({ quotationId, quotationName, drawingName }),
					},
					{
						key: "downloadDetailsWord",
						label: isWordPendingForThisRow ? DETAILS_WORD_MESSAGES.pending : DETAILS_WORD_MESSAGES.menuItem,
						icon: <DownloadIcon className={`${dropdownIcon} ${dropdownIconDownload}`} strokeColor="black" />,
						// One Word-file generation at a time — independent of the CSV's and PDF's flags.
						disabled: wordBusyQuotationId !== null,
						onClick: () => downloadDetailsWord({ quotationId, quotationName, drawingName }),
					},
				]

		return downloadItems.length
			? [...ownerItems, { key: "csvDivider", type: "divider" }, ...downloadItems]
			: ownerItems
	}

	return { getDropdownItems }
}
